"use client";

// src/index.tsx
import * as React from "react";
import {
  renderInnerHTML,
  formatToData,
  NumberFlowLite,
  prefersReducedMotion as _prefersReducedMotion,
  canAnimate as _canAnimate,
  define
} from "number-flow";
import { BROWSER } from "esm-env";
var REACT_MAJOR = parseInt(React.version.match(/^(\d+)\./)?.[1]);
var isReact19 = REACT_MAJOR >= 19;
var OBSERVED_ATTRIBUTES = ["data", "digits"];
var NumberFlowElement = class extends NumberFlowLite {
  attributeChangedCallback(attr, _oldValue, newValue) {
    this[attr] = JSON.parse(newValue);
  }
};
NumberFlowElement.observedAttributes = isReact19 ? [] : OBSERVED_ATTRIBUTES;
define("number-flow-react", NumberFlowElement);
var formatters = {};
var serialize = isReact19 ? (p) => p : JSON.stringify;
function splitProps(props) {
  const {
    transformTiming,
    spinTiming,
    opacityTiming,
    animated,
    respectMotionPreference,
    trend,
    continuous,
    ...rest
  } = props;
  return [
    {
      transformTiming,
      spinTiming,
      opacityTiming,
      animated,
      respectMotionPreference,
      trend,
      continuous
    },
    rest
  ];
}
var NumberFlowImpl = class extends React.Component {
  constructor(props) {
    super(props);
    this.handleRef = this.handleRef.bind(this);
  }
  // Update the non-`data` props to avoid JSON serialization
  // Data needs to be set in render still:
  updateProperties(prevProps) {
    if (!this.el) return;
    this.el.manual = !this.props.isolate;
    const [nonData] = splitProps(this.props);
    Object.entries(nonData).forEach(([k, v]) => {
      this.el[k] = v ?? NumberFlowElement.defaultProps[k];
    });
    if (prevProps?.onAnimationsStart)
      this.el.removeEventListener("animationsstart", prevProps.onAnimationsStart);
    if (this.props.onAnimationsStart)
      this.el.addEventListener("animationsstart", this.props.onAnimationsStart);
    if (prevProps?.onAnimationsFinish)
      this.el.removeEventListener("animationsfinish", prevProps.onAnimationsFinish);
    if (this.props.onAnimationsFinish)
      this.el.addEventListener("animationsfinish", this.props.onAnimationsFinish);
  }
  componentDidMount() {
    this.updateProperties();
    if (isReact19 && this.el) {
      this.el.digits = this.props.digits;
      this.el.data = this.props.data;
    }
  }
  getSnapshotBeforeUpdate(prevProps) {
    this.updateProperties(prevProps);
    if (prevProps.data !== this.props.data) {
      if (this.props.group) {
        this.props.group.willUpdate();
        return () => this.props.group?.didUpdate();
      }
      if (!this.props.isolate) {
        this.el?.willUpdate();
        return () => this.el?.didUpdate();
      }
    }
    return null;
  }
  componentDidUpdate(_, __, didUpdate) {
    didUpdate?.();
  }
  handleRef(el) {
    if (this.props.innerRef) this.props.innerRef.current = el;
    this.el = el;
  }
  render() {
    const [
      _,
      {
        innerRef,
        className,
        data,
        willChange,
        isolate,
        group,
        digits,
        onAnimationsStart,
        onAnimationsFinish,
        ...rest
      }
    ] = splitProps(this.props);
    return (
      // @ts-expect-error missing types
      /* @__PURE__ */ React.createElement(
        "number-flow-react",
        {
          ref: this.handleRef,
          "data-will-change": willChange ? "" : void 0,
          class: className,
          "aria-label": data.valueAsString,
          ...rest,
          role: "img",
          dangerouslySetInnerHTML: { __html: BROWSER ? "" : renderInnerHTML(data) },
          suppressHydrationWarning: true,
          digits: serialize(digits),
          data: serialize(data)
        }
      )
    );
  }
};
var NumberFlow = React.forwardRef(function NumberFlow2({ value, locales, format, prefix, suffix, ...props }, _ref) {
  React.useImperativeHandle(_ref, () => ref.current, []);
  const ref = React.useRef();
  const group = React.useContext(NumberFlowGroupContext);
  group?.useRegister(ref);
  const localesString = React.useMemo(() => locales ? JSON.stringify(locales) : "", [locales]);
  const formatString = React.useMemo(() => format ? JSON.stringify(format) : "", [format]);
  const data = React.useMemo(() => {
    const formatter = formatters[`${localesString}:${formatString}`] ??= new Intl.NumberFormat(
      locales,
      format
    );
    return formatToData(value, formatter, prefix, suffix);
  }, [value, localesString, formatString, prefix, suffix]);
  return /* @__PURE__ */ React.createElement(NumberFlowImpl, { ...props, group, data, innerRef: ref });
});
var src_default = NumberFlow;
var NumberFlowGroupContext = React.createContext(void 0);
function NumberFlowGroup({ children }) {
  const flows = React.useRef(/* @__PURE__ */ new Set());
  const updating = React.useRef(false);
  const pending = React.useRef(/* @__PURE__ */ new WeakMap());
  const value = React.useMemo(
    () => ({
      useRegister(ref) {
        React.useEffect(() => {
          flows.current.add(ref);
          return () => {
            flows.current.delete(ref);
          };
        }, []);
      },
      willUpdate() {
        if (updating.current) return;
        updating.current = true;
        flows.current.forEach((ref) => {
          const f = ref.current;
          if (!f || !f.created) return;
          f.willUpdate();
          pending.current.set(f, true);
        });
      },
      didUpdate() {
        flows.current.forEach((ref) => {
          const f = ref.current;
          if (!f || !pending.current.get(f)) return;
          f.didUpdate();
          pending.current.delete(f);
        });
        updating.current = false;
      }
    }),
    []
  );
  return /* @__PURE__ */ React.createElement(NumberFlowGroupContext.Provider, { value }, children);
}
var useIsSupported = () => React.useSyncExternalStore(
  () => () => {
  },
  // this value doesn't change, but it's useful to specify a different SSR value:
  () => _canAnimate,
  () => false
);
var usePrefersReducedMotion = () => React.useSyncExternalStore(
  (cb) => {
    _prefersReducedMotion?.addEventListener("change", cb);
    return () => _prefersReducedMotion?.removeEventListener("change", cb);
  },
  () => _prefersReducedMotion.matches,
  () => false
);
function useCanAnimate({ respectMotionPreference = true } = {}) {
  const isSupported = useIsSupported();
  const reducedMotion = usePrefersReducedMotion();
  return isSupported && (!respectMotionPreference || !reducedMotion);
}
export {
  NumberFlowElement,
  NumberFlowGroup,
  src_default as default,
  useCanAnimate,
  useIsSupported,
  usePrefersReducedMotion
};
